import {isEmpty, KPI, Statistic} from "weed-js";
import {CategoricalKPI} from "weed-js";
import React from "react";
import {KPIPill} from "./KPIPill";


export const getItemSalesDetails = (item, inventoryByProductId) => {
    const inventory = inventoryByProductId[item.product_id];

    let itemProfit = 0;
    let itemRevenue = 0;
    let itemDiscount = 0;
    let itemUnitPrice = 0;
    let itemUnitCost = 0;

    const itemQuantity = parseFloat(item.quantity) || 1;

    itemUnitPrice = (item.unit_price ? item.unit_price : (inventory ? inventory.price : 0)) / itemQuantity;
    itemUnitCost = (item.unit_cost ? item.unit_cost : (inventory ? inventory.unit_cost : 0)) / itemQuantity;

    if (item.total_discount) {
        itemDiscount = (item.total_discount / itemQuantity);
    }
    if (itemQuantity && itemUnitPrice && itemUnitCost) {
        itemProfit = itemQuantity * (itemUnitPrice - itemUnitCost) - itemDiscount;
    }
    if (itemQuantity && itemUnitPrice) {
        itemRevenue = itemQuantity * itemUnitPrice - itemDiscount;
    }

    return {
        profit: itemProfit,
        revenue: itemRevenue,
        discount: itemDiscount,
        unitPrice: itemUnitPrice,
        unitCost: itemUnitCost
    };
};
const uniqueTxnIds = new Set();
export const incrementStats = (kpis, item, inventoryByProductId, targetProductId) => {
    if (!item?.transaction?.transaction_date) {
        console.error(item);
        throw new Error("Transaction date was undefined!");
    }

    const {profit, revenue, discount, unitCost, unitPrice} = getItemSalesDetails(item, inventoryByProductId);
    const common = {
        date: item.transaction.transaction_date,
        isSample: item.product_id === targetProductId
    };

    if (discount) {
        kpis.discountOverTime.add({...common, value: discount});
    }
    kpis.percentileBySales.add({...common, value: revenue});
    kpis.unitPrice.add({...common, value: unitPrice});
    kpis.unitCost.add({...common, value: unitCost});
    kpis.profitOverTime.add({...common, value: profit});
    kpis.revenueOverTime.add({...common, value: revenue});
    kpis.marginPercentOverTime.add({
        ...common, value: parseFloat(item.quantity) * (unitPrice - unitCost) / (parseFloat(item.quantity) * unitPrice),
    });
    if (item.product_id === targetProductId) {
        uniqueTxnIds.add(item.transaction.id);
    }
    kpis.transactionsOverTime.add({...common, value: item.transaction.id});
    kpis.unitsSoldOverTime.add({...common, value: parseFloat(item.quantity)});
    if (item.transaction.transaction_type !== null) {
        kpis.transactionType.add({...common, value: item.transaction.id, category: item.transaction.transaction_type});
    }
    if (item.transaction.order_method !== null) {
        kpis.orderMethod.add({...common, value: item.transaction.id, category: item.transaction.order_method});
    }
    if (item.transaction.order_source !== null) {
        kpis.orderSource.add({...common, value: item.transaction.id, category: item.transaction.order_source});
    }
    if (item.transaction.order_type !== null) {
        kpis.orderType.add({...common, value: item.transaction.id, category: item.transaction.order_type});
    }
    return kpis;
};

export const calculateTimeWindowedKPIs = ({currentProduct, transactionItems, inventoryByProductId, previousWindow, currentWindow, incrementalCallback, onComplete}) => {
    if (isEmpty(previousWindow)) {
        throw new Error("Previous window must be defined to calculate KPIs");
    }
    if (isEmpty(currentWindow)) {
        throw new Error("Current window must be defined to calculate KPIs");
    }
    const common = {
        sampleSeriesTitle: currentProduct?.name,
        universeSeriesTitle: "Global Baseline",
    };
    const average = {
        operation: Statistic.AVERAGE,
    };
    const percentile = {
        operation: Statistic.PERCENTILE,
    };
    const sum = {
        operation: Statistic.SUM,
    };
    const unique = {
        operation: Statistic.UNIQUE,
    };
    const empty = {
        percentileBySales: new KPI({
            title: "Net Sales Rank",
            ...common,
            ...percentile,
            dataType: Statistic.PERCENTILE,
            previousWindow,
            currentWindow
        }),
        unitPrice: new KPI({
            title: "Unit Price", ...common, ...average,
            dataType: Statistic.CURRENCY,
            previousWindow,
            currentWindow
        }),
        unitCost: new KPI({
            title: "Unit Cost", ...common, ...average,
            dataType: Statistic.CURRENCY,
            previousWindow,
            currentWindow
        }),
        discountOverTime: new KPI({
            title: "Average Discount", ...common, ...average,
            dataType: Statistic.CURRENCY,
            previousWindow,
            currentWindow
        }),
        profitOverTime: new KPI({
            title: "Average Profit", ...common, ...average,
            dataType: Statistic.CURRENCY,
            previousWindow,
            currentWindow
        }),
        revenueOverTime: new KPI({
            title: "Revenue", ...common, ...sum,
            dataType: Statistic.CURRENCY,
            previousWindow,
            currentWindow
        }),
        marginPercentOverTime: new KPI({
            title: "Average Margin", ...common, ...average,
            dataType: Statistic.PERCENT,
            previousWindow,
            currentWindow
        }),
        transactionsOverTime: new KPI({
            title: "Transactions", ...common, ...unique,
            dataType: Statistic.NUMBER,
            previousWindow,
            currentWindow
        }),
        unitsSoldOverTime: new KPI({
            title: "Units Sold",
            ...common,
            ...sum,
            dataType: Statistic.NUMBER,
            previousWindow,
            currentWindow
        }),
        orderSource: new CategoricalKPI({
            title: "Order Source",
            ...common,
            operation: Statistic.UNIQUE,
            dataType: CategoricalKPI.NUMBER,
            previousWindow, currentWindow
        }),
        orderType: new CategoricalKPI({
            title: "Order Type",
            ...common,
            operation: Statistic.UNIQUE,
            dataType: CategoricalKPI.NUMBER,
            previousWindow, currentWindow
        }),
        orderMethod: new CategoricalKPI({
            title: "Order Method",
            ...common,
            operation: Statistic.UNIQUE,
            dataType: CategoricalKPI.NUMBER,
            previousWindow, currentWindow
        }),
        transactionType: new CategoricalKPI({
            title: "Transaction Type",
            ...common,
            operation: Statistic.UNIQUE,
            dataType: CategoricalKPI.NUMBER,
            previousWindow, currentWindow
        }),
    };
    if (
        isEmpty(currentProduct) ||
        isEmpty(transactionItems) ||
        isEmpty(inventoryByProductId)
    ) {
        return empty;
    }

    const sorted = transactionItems.sort((a, b) => {
        return new Date(a.transaction_date) - new Date(b.transaction_date);
    });

    async function processKPISAsync(kpis, items) {
        for (let i=0; i<items.length; i++) {
            const item = items[i];
            incrementStats(kpis, item, inventoryByProductId, currentProduct.id);
            if (i % 1000 === 0) {
                console.log("Processed", i, "items for KPIs");
                incrementalCallback?.(i, items.length);
                await new Promise(resolve => setTimeout(resolve, 0)); // Yield to event loop
            }
        }
        console.log("Finished processing", items.length, "items for KPIs");
        onComplete(kpis);
        return kpis;
    }

    return processKPISAsync(empty, sorted);

    // const kpis = sorted.reduce((kpis, [item, idx]) => {
    //     return incrementStats(kpis, item, inventoryByProductId, currentProduct.id);
    // }, empty);

    // return kpis;
};

export const getTimeWindowedKPIPillColumns = ({
    kpis,
    previousWindow,
    currentWindow,
    showBaseline = true,
    showComparison = true,
    showBaselineVSComparison = true
}) => {

    // if (isEmpty(product)) {
    //     console.warn("Product is empty, returning empty columns");
    //     return {currentColumn: [], previousColumn: [], previousToCurrent: []};
    // }

    // const kpis = calculateTimeWindowedKPIs({
    //     currentProduct: product,
    //     transactionItems: transactionItems,
    //     inventoryByProductId: inventoryByProductId,
    //     previousWindow: previousWindow,
    //     currentWindow: currentWindow,
    // });

    return Object.entries(kpis).reduce((acc, [key, kpi]) => {
        if (kpi instanceof CategoricalKPI) {
            return acc;
        }

        const trend = kpi.trend();
        const {previous, current, previousToCurrent} = kpi.displayValue();

        if (showBaseline) {
            acc.previousColumn.push(getKPIPill({
                title: kpi.title,
                sampleValue: previous.sample,
                universeValue: previous.universe,
                arrow: trend.sample.previous.arrow(),
                globalArrow: trend.universe.previous.arrow(),
                className: trend.sample.previous.className(),
                fromValue: trend.sample.previous.firstHalf.sample,
                toValue: trend.sample.previous.secondHalf.sample,
                N1: trend.sample.previous.testResult.baselineN,
                N2: trend.sample.previous.testResult.comparisonN,
                sigma1: trend.sample.previous.testResult.baselineSigma,
                sigma2: trend.sample.previous.testResult.comparisonSigma,
                pValue: trend.sample.previous.testResult.pValue,
                mu1: trend.sample.previous.testResult.baselineMean,
                mu2: trend.sample.previous.testResult.comparisonMean,
                key: `${kpi.title}-previous`
            }));
        }

        if (showComparison) {
            acc.currentColumn.push(getKPIPill({
                title: kpi.title,
                sampleValue: current.sample,
                universeValue: current.universe,
                arrow: trend.sample.current.arrow(),
                globalArrow: trend.universe.current.arrow(),
                className: trend.sample.current.className(),
                fromValue: trend.sample.current.firstHalf.sample,
                toValue: trend.sample.current.secondHalf.sample,
                N1: trend.sample.current.testResult.baselineN,
                N2: trend.sample.current.testResult.comparisonN,
                sigma1: trend.sample.current.testResult.baselineSigma,
                sigma2: trend.sample.current.testResult.comparisonSigma,
                pValue: trend.sample.current.testResult.pValue,
                mu1: trend.sample.current.testResult.baselineMean,
                mu2: trend.sample.current.testResult.comparisonMean,
                key: `${kpi.title}-current`
            }));
        }

        if (showBaselineVSComparison) {
            acc.previousToCurrent.push(getKPIPill({
                title: kpi.title,
                sampleValue: previousToCurrent.sample,
                universeValue: previousToCurrent.universe,
                arrow: trend.sample.previousToCurrent.arrow(),
                globalArrow: trend.universe.previousToCurrent.arrow(),
                className: trend.sample.previousToCurrent.className(),
                fromValue: trend.sample.previousToCurrent.firstHalf.sample,
                toValue: trend.sample.previousToCurrent.secondHalf.sample,
                N1: trend.sample.previousToCurrent.testResult.baselineN,
                N2: trend.sample.previousToCurrent.testResult.comparisonN,
                sigma1: trend.sample.previousToCurrent.testResult.baselineSigma,
                sigma2: trend.sample.previousToCurrent.testResult.comparisonSigma,
                mu1: trend.sample.previousToCurrent.testResult.baselineMean,
                mu2: trend.sample.previousToCurrent.testResult.comparisonMean,
                pValue: trend.sample.previousToCurrent.testResult.pValue,
                key: `${kpi.title}-previousToCurrent`
            }));
        }

        return acc;
    }, {currentColumn: [], previousColumn: [], previousToCurrent: []});
}
export const getKPIPill = ({
    title,
    sampleValue,
    universeValue,
    arrow,
    globalArrow,
    fromValue,
    toValue,
    className,
    N1,
    N2,
    sigma1,
    sigma2,
    pValue,
    key,
    mu1,
    mu2
}) => {
    return <KPIPill
        title={title}
        sampleValue={sampleValue}
        universeValue={universeValue}
        arrow={arrow}
        globalArrow={globalArrow}
        className={className}
        fromValue={fromValue}
        toValue={toValue}
        N1={N1}
        N2={N2}
        mu1={mu1}
        mu2={mu2}
        sigma1={sigma1}
        sigma2={sigma2}
        pValue={pValue}
        key={key}
    />
}

export const windowsAreDefined = (windows) => {
    if (windows?.previous?.start && windows.previous.end) {
        if (windows.current?.start && windows.current.end) {
            return true;
        }
    }
    return false;
}